import { getPortraitUrl, getPortraitTuning } from '../../combat/spriteConfig';

export interface UnitPortraitOptions {
  spriteKey: string;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Optional scale multiplier — applied as CSS transform-origin bottom + scale */
  scale?: number;
}

/**
 * Reference container size that `portraitOffsetX/Y` are tuned against in the
 * dev portrait editor (`.ue-portrait-frame` is 120×120). At render time we
 * convert the px-at-reference value into a percentage so the translate scales
 * with whatever container the portrait actually lands in (combat HUD ~50-70px,
 * briefing 48px, etc.). Without this conversion, a +53px tuning that frames
 * the face in a 120px box pushes the face entirely out of a 50px box.
 *
 * The translate uses %-units which resolve against the element's own size;
 * the img is `width:100%; height:100%`, so % = % of container.
 */
export const PORTRAIT_REFERENCE_PX = 120;

/** Convert per-sprite tuning into the inline transform that frames the face.
 *  Shared by renderUnitPortrait, the dev editor, and dialogue scenes so all
 *  three contexts produce visually identical crops at any container size. */
export function buildPortraitTransform(tuning: { scale?: number; offsetX?: number; offsetY?: number }): string {
  const s = tuning.scale ?? 2.4;
  const tx = tuning.offsetX ?? 0;
  const ty = tuning.offsetY ?? 0;
  const txPct = (tx / PORTRAIT_REFERENCE_PX) * 100;
  const tyPct = (ty / PORTRAIT_REFERENCE_PX) * 100;
  return `translate(${txPct.toFixed(3)}%, ${tyPct.toFixed(3)}%) scale(${s})`;
}

export function renderUnitPortrait(options: UnitPortraitOptions): string {
  const { spriteKey, name, size = 'md', className = '', scale } = options;
  const src = getPortraitUrl(spriteKey);
  const classes = `unit-portrait unit-portrait--${size} ${className}`.trim();

  let style = '';
  if (size === 'sm') {
    // Per-sprite portrait tuning. portraitOffsetX/Y are PX measured against a
    // 120px reference container (PORTRAIT_REFERENCE_PX); we convert to % so the
    // crop is identical across containers of any size.
    const tuning = getPortraitTuning(spriteKey);
    if (tuning.scale != null || tuning.offsetX != null || tuning.offsetY != null) {
      style = ` style="transform:${buildPortraitTransform(tuning)}"`;
    }
  } else if (scale && scale !== 1) {
    style = ` style="transform:scale(${scale});transform-origin:bottom center"`;
  }

  return `<img class="${classes}" src="${src}" alt="${name}" draggable="false"${style}>`;
}
